import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Box, Stack, Typography } from '@packages/shared';
import { RatingTagsFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingTagsFragmentFragmentDoc';
import { useIntl } from 'react-intl';
import type { RatingTagSkuFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingTagSkuFragmentFragmentDoc';
import { ratingTagsMessages } from './messages';
import { RatingTag } from '../RatingTag';

/* GraphQL */ `
  fragment RatingTagsFragment on RatingTags {
    pro {
      ...RatingTagFragment
    }
    contra {
      ...RatingTagFragment
    }
  }
`;

export type RatingTagsProps = {
  /**
   * rating tags data with pro and contra tags
   */
  ratingTagsData: FragmentType<typeof RatingTagsFragmentFragmentDoc>;
  /**
   * sku needed for product in rating page
   */
  skuData: FragmentType<typeof RatingTagSkuFragmentFragmentDoc>;
};

/**
 * List of pro and contra Chips in rating overview
 */
export const RatingTags = ({ ratingTagsData, skuData }: RatingTagsProps) => {
  const { pro, contra } = getFragmentData(RatingTagsFragmentFragmentDoc, ratingTagsData);
  const { formatMessage } = useIntl();

  if (pro?.length === 0 && contra?.length === 0) return null;

  return (
    <Stack spacing={1.5}>
      {[pro, contra].map((tags, index) =>
        tags && tags.length > 0 ? (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={`${index}`}>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
              {formatMessage(
                index === 0 ? ratingTagsMessages.headlinePro : ratingTagsMessages.headlineContra,
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, m: 0, p: 0 }} component="ul">
              {tags.map((tag, idx) => (
                <RatingTag
                  tagData={tag}
                  skuData={skuData}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}${idx}`}
                  category={index === 0 ? 'Vorteile' : 'Nachteile'}
                />
              ))}
            </Box>
          </Box>
        ) : null,
      )}
    </Stack>
  );
};
